import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { grantResourceAccess } from '../../store/actions/Projects/ProjectActions';
import ProfileImage from '../Header/ProfileImage';
import Button from '../shared/TailwindComponents/Button';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import Dropdown, { DropdownOption } from '../shared/TailwindComponents/Dropdown';
import { normalise } from '../shared/access/accessModel';
import {
  PiLockKeyLight,
  PiCheckCircleLight,
} from 'react-icons/pi';

interface CollaboratorAccessInfo {
  user_id: number;
  name: string;
  email: string;
  uuid: string;
  initials: string;
  profile_picture?: string;
  has_access: boolean;
  access_level: string | null;
  project_access?: string;
}

interface AccessReviewDialogProps {
  projectUuid: string;
  resourceType: string;
  resourceUuid: string;
  resourceLabel: string;
  collaborators: CollaboratorAccessInfo[];
  canGrantAccess: boolean;
  onClose: () => void;
  onAccessGranted?: () => void;
}

const ACCESS_OPTIONS: DropdownOption[] = [
  { value: 'view', label: 'View' },
  { value: 'edit', label: 'Edit' },
];

const AccessReviewDialog: React.FC<AccessReviewDialogProps> = ({
  projectUuid,
  resourceType,
  resourceUuid,
  resourceLabel,
  collaborators,
  canGrantAccess,
  onClose,
  onAccessGranted,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [isGranting, setIsGranting] = useState(false);

  const usersWithoutAccess = collaborators.filter((c) => !c.has_access);
  const usersWithAccess = collaborators.filter((c) => c.has_access);

  // Initialize per-user access from each user's project_access level:
  // if they already have edit/admin on the project, default their resource grant to 'edit'.
  const [perUserAccess, setPerUserAccess] = useState<Record<number, 'view' | 'edit'>>(() => {
    const map: Record<number, 'view' | 'edit'> = {};
    for (const u of usersWithoutAccess) {
      map[u.user_id] = (u.project_access === 'edit' || u.project_access === 'admin') ? 'edit' : 'view';
    }
    return map;
  });

  const toggleUser = (userId: number) => {
    setSelectedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => {
    if (selectedUsers.size === usersWithoutAccess.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(usersWithoutAccess.map((u) => u.user_id)));
    }
  };

  const setAllSelectedAccess = (level: 'view' | 'edit') => {
    setPerUserAccess((prev) => {
      const next = { ...prev };
      for (const userId of selectedUsers) {
        next[userId] = level;
      }
      return next;
    });
  };

  const handleGrantAccess = useCallback(async () => {
    if (selectedUsers.size === 0) return;
    setIsGranting(true);
    const grants = Array.from(selectedUsers).map((userId) => ({
      user_id: userId,
      access: perUserAccess[userId] ?? 'view',
    }));
    const result = await dispatch(grantResourceAccess(projectUuid, resourceType, resourceUuid, grants));
    setIsGranting(false);
    if (result) {
      onAccessGranted?.();
      onClose();
    }
  }, [selectedUsers, perUserAccess, dispatch, projectUuid, resourceType, resourceUuid, onAccessGranted, onClose]);

  return (
    <div className="p-5 space-y-4 max-w-md">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-warning-50 dark:bg-warning-900/20 flex items-center justify-center">
          <PiLockKeyLight className="w-4 h-4 text-warning-500" />
        </div>
        <div>
          <p className="text-sm text-gray-700 dark:text-gray-300 font-medium">
            {t('This {{type}} stays private by default.', { type: resourceLabel })}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {usersWithoutAccess.length === 1
              ? t('1 project member will NOT see this {{type}} unless you grant them access below.', { type: resourceLabel })
              : t('{{count}} project members will NOT see this {{type}} unless you grant them access below.', { count: usersWithoutAccess.length, type: resourceLabel })}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            {canGrantAccess
              ? t('Access you grant here is tied to the project - it will be revoked automatically if the resource is unlinked or the member leaves the project.')
              : t('Only the resource owner/admin can grant access to other users.')}
          </p>
        </div>
      </div>

      {/* Users without access */}
      {usersWithoutAccess.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500">
              {t('No access')}
            </span>
            {canGrantAccess && (
              <div className="flex items-center gap-2">
                {/* "Set all to" bulk-level picker - only when 2+ users are selected */}
                {selectedUsers.size > 1 && (
                  <div className="flex items-center gap-1">
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                      {t('Set all to')}
                    </span>
                    <Dropdown
                      options={ACCESS_OPTIONS}
                      value=""
                      onChange={(val) => {
                        const level = (Array.isArray(val) ? val[0] : val) as 'view' | 'edit';
                        if (level) setAllSelectedAccess(level);
                      }}
                      compact
                      placeholder="..."
                    />
                  </div>
                )}
                <button
                  onClick={selectAll}
                  className="text-[11px] text-primary-600 dark:text-primary-400 hover:underline"
                >
                  {selectedUsers.size === usersWithoutAccess.length ? t('Deselect all') : t('Select all')}
                </button>
              </div>
            )}
          </div>

          {usersWithoutAccess.map((user) => (
            <div
              key={user.user_id}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${canGrantAccess ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30' : ''} transition-colors`}
              onClick={canGrantAccess ? () => toggleUser(user.user_id) : undefined}
            >
              {canGrantAccess && (
                // Stop the click from bubbling to the row's onClick - otherwise
                // the row handler runs *and* the change handler runs, producing
                // two toggles that net out to no change (the checkbox feels
                // "locked"). The change handler alone updates state.
                <span onClick={(e) => e.stopPropagation()}>
                  <CheckBox
                    label={user.name || user.email}
                    hideLabel
                    translate={false}
                    checked={selectedUsers.has(user.user_id)}
                    onChange={() => toggleUser(user.user_id)}
                  />
                </span>
              )}
              <ProfileImage
                profilePicture={normalise(user).avatar}
                email={user.email}
                initials={user.initials || '??'}
                name={user.name}
                size="w-6 h-6"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300 truncate flex-1">{user.name}</span>
              {canGrantAccess ? (
                /* Per-user access level picker - stopPropagation so clicking the
                   dropdown doesn't toggle the checkbox on the row. */
                <div onClick={(e) => e.stopPropagation()}>
                  <Dropdown
                    options={ACCESS_OPTIONS}
                    value={perUserAccess[user.user_id] ?? 'view'}
                    onChange={(val) => {
                      const level = (Array.isArray(val) ? val[0] : val) as 'view' | 'edit';
                      setPerUserAccess((prev) => ({ ...prev, [user.user_id]: level }));
                    }}
                    compact
                  />
                </div>
              ) : (
                <PiLockKeyLight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Users with access */}
      {usersWithAccess.length > 0 && (
        <div className="space-y-1">
          <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 block mb-2">
            {t('Has access')}
          </span>
          {usersWithAccess.map((user) => (
            <div key={user.user_id} className="flex items-center gap-2.5 px-3 py-2 opacity-60">
              <div className="w-3.5" />
              <ProfileImage
                profilePicture={normalise(user).avatar}
                email={user.email}
                initials={user.initials || '??'}
                name={user.name}
                size="w-6 h-6"
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 truncate flex-1">{user.name}</span>
              <PiCheckCircleLight className="w-3.5 h-3.5 text-success-500 flex-shrink-0" />
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end space-x-4 px-4 mt-6 pt-6 border-t border-gray-300 dark:border-gray-700">
        <Button variant="neutral" size="small" onClick={onClose} className="w-24">
          {canGrantAccess ? t('Skip') : t('Close')}
        </Button>
        {canGrantAccess && (
          <Button
            variant="primary"
            size="small"
            onClick={handleGrantAccess}
            disabled={selectedUsers.size === 0 || isGranting}
          >
            {isGranting ? t('Granting...') : selectedUsers.size > 0
              ? t('Grant access ({{count}})', { count: selectedUsers.size })
              : t('Grant access')}
          </Button>
        )}
      </div>
    </div>
  );
};

AccessReviewDialog.displayName = 'AccessReviewDialog';

export default AccessReviewDialog;
